import { type Ref, computed, nextTick, ref } from "vue";
import { defineStore } from "pinia";
import { Page, TaskCenter, TaskCenterQueryParams } from "@supplier/entity";
import { ElMessage } from "element-plus";
import { TASK_STATUS, YES_OR_NO } from "@supplier/meta";
import { findTaskCenterItemIndex } from "@supplier/utils";
import { deleteAll, download, markRead, paging, deleteTask as taskDeleteTask } from "@/api/taskService";
import { useUserStore } from "@/stores/user";

/**
 * 任务中心打开状态枚举
 */
enum TaskCenterOpenStateEnum {
    /**
     * 关闭
     */
    Close,
    /**
     * 打开
     */
    Open
}

/**
 * 任务事件类型
 */
enum TaskEventTypeEnum {
    /**
     * 任务中心-任务状态改变
     */
    TASK_CHANGE = "TASK_CHANGE",
    /**
     * 任务中心-新建任务
     */
    TASK_NEW = "TASK_NEW",
    /**
     * 任务中心-删除任务
     */
    TASK_DELETE = "TASK_DELETE",
    /**
     * 任务中心-清空任务
     */
    TASK_CLEANUP = "TASK_CLEANUP"
}

const STORE_NAME = "zhongsheng-user-task-center";
// 翻页的每页行数
const DEFAULT_TASK_NUM = 10;
export const useTaskCenterStore = defineStore(STORE_NAME, () => {
    const userStore = useUserStore();
    // 搜索关键词
    const searchKeyword = ref<undefined | string>();
    const setKeyword = (keyword: string) => {
        searchKeyword.value = keyword;
    };
    /**
     * 数据是否加载中
     */
    const isLoading = ref(false);

    // 显示红点标记
    const showNotice = ref(false);

    /**
     * 任务中心数据
     */
    const _taskList: Ref<TaskCenter[]> = ref<TaskCenter[]>([]);

    const taskList = computed<TaskCenter[]>(() => {
        return _taskList.value;
    });

    const _listPage = ref(1);

    const listPage = computed<number>({
        get: () => {
            return _listPage.value;
        },
        set: (page: number) => {
            _listPage.value = page;
        }
    });

    const isLoadMore = ref(false);
    const setLoadMore = (loadMore: boolean) => {
        isLoadMore.value = loadMore;
    };

    const initListPage = () => {
        _listPage.value = 1;
    };
    const createEmptyTaskPage = (size: number): Page<TaskCenter> => ({
        list: [],
        currentPage: listPage.value,
        pageSize: size,
        total: 0,
        totalPage: 0
    });

    const getTaskList = async (size: number = 10, push: boolean = true) => {
        isLoading.value = true;
        if (!userStore.token) {
            isLoading.value = false;
            _totalTask.value = 0;
            if (!push) clearTaskList();
            return createEmptyTaskPage(size);
        }
        const params: TaskCenterQueryParams = {
            size: isLoadMore.value ? 10 : size,
            page: listPage.value,
            sortKey: "created_time",
            sortOrder: "desc",
            fileName: undefined
        };
        if (searchKeyword.value) params.fileName = searchKeyword.value;

        const data = await paging(params);
        _totalTask.value = data.total;
        if (push) {
            _taskList.value.push(...data.list) as unknown as TaskCenter[];
        } else {
            _taskList.value = data.list;
        }
        isLoading.value = false;
        return data;
    };

    const clearTaskList = () => {
        showNotice.value = false;
        initListPage();
        _taskList.value = [];
    };

    const remoteClearTaskList = async () => {
        showNotice.value = false;
        await deleteAll();
        _totalTask.value = 0;
        clearTaskList();
    };

    const deleteTask = async (taskId: number) => {
        await taskDeleteTask(taskId);
        const index = _taskList.value.findIndex((task: TaskCenter) => task.id === taskId);
        if (index > -1) {
            _taskList.value.splice(index, 1);
        }

        ElMessage.success("删除成功");
    };

    // 定时器
    let unReadMsgIntervalId: ReturnType<typeof setInterval> | undefined;
    // 轮询拉取未读消息 标记红点
    const getUnReadMsgNum = async () => {
        if (!userStore.token) {
            clearTaskList();
            return;
        }
        const { list } = await getTaskList(isLoadMore.value ? 10 : 3, false);
        if (taskCenterOpenState.value === TaskCenterOpenStateEnum.Open) {
            showNotice.value = false;
        } else if (
            list.some((task: TaskCenter) => task.status !== TASK_STATUS.NOT_COMPLETED.code && task.isRead === YES_OR_NO.NO.code)
        ) {
            // 不是未完成状态 并且 未读
            showNotice.value = true;
        }
    };
    // 下载文件
    const downloadTaskFile = async (task: TaskCenter) => {
        // 物流导入失败任务的异常文件由后端按任务 ID 下载，文件名优先使用业务名称兜底。
        await download(task.id, task.businessName || task.fileName || "异常数据");
    };

    /**
     * 任务中心开启状态
     */
    const taskCenterOpenState = ref<TaskCenterOpenStateEnum>(TaskCenterOpenStateEnum.Close);
    const openTaskCenter = (showMessage: boolean = true) => {
        if (!userStore.token) {
            clearTaskList();
            return;
        }
        if (showMessage) ElMessage.success("创建任务成功");
        clearTaskList();
        markRead().catch(() => undefined);
        taskCenterOpenState.value = TaskCenterOpenStateEnum.Open;
        // 首次打开只加载3条数据
        getTaskList(3).catch(() => undefined);
        setTimeout(() => {
            if (
                !_taskList.value.some(
                    (task: TaskCenter) => task.status === TASK_STATUS.NOT_COMPLETED.code && task.isRead === YES_OR_NO.NO.code
                )
            ) {
                showNotice.value = false;
            }
        }, 10);
        clearInterval(unReadMsgIntervalId);
        // 自动刷新列表
        unReadMsgIntervalId = setInterval(getUnReadMsgNum, 5000); // 每隔5秒执行一次
    };
    const closeTaskCenter = () => {
        taskCenterOpenState.value = TaskCenterOpenStateEnum.Close;
        isLoadMore.value = false;
        isLoading.value = false;
        nextTick(() => {
            if (!userStore.token) return;
            markRead().catch(() => undefined);
            clearTaskList();
        }).catch(() => undefined);
        clearInterval(unReadMsgIntervalId);
        if (userStore.token) {
            unReadMsgIntervalId = setInterval(getUnReadMsgNum, 30000); // 每隔30秒执行一次
        } else {
            unReadMsgIntervalId = undefined;
        }
    };
    const isTaskCenterOpen = computed(() => {
        return taskCenterOpenState.value === TaskCenterOpenStateEnum.Open;
    });

    /**
     * 任务中心消息总数
     */
    const _totalTask = ref<number>(0);

    const totalTask = computed({
        get: () => {
            return _totalTask.value;
        },
        set: (value: number) => (_totalTask.value = value)
    });

    // 任务中心入口数字徽标文案：0 不显示，100 条及以上统一展示 99+。
    const taskBadgeText = computed(() => {
        if (_totalTask.value <= 0) return "";
        if (_totalTask.value >= 100) return "99+";
        return String(_totalTask.value);
    });

    const resetTaskCenterOnLogout = () => {
        // 退出登录只做本地状态清理，避免 token 清空后继续触发任务中心接口。
        clearInterval(unReadMsgIntervalId);
        unReadMsgIntervalId = undefined;
        taskCenterOpenState.value = TaskCenterOpenStateEnum.Close;
        isLoadMore.value = false;
        isLoading.value = false;
        _totalTask.value = 0;
        clearTaskList();
    };

    const initTotalTask = async () => {
        /**
         * 每次请求都会更新total值
         */
        await getTaskList(0);
    };

    const hasMoreTask = computed(() => {
        return !(_taskList.value.length >= _totalTask.value);
    });

    const updateTask = (taskEventType: TaskEventTypeEnum, task: TaskCenter) => {
        if (isTaskCenterOpen.value) {
            const taskItemIndex = findTaskCenterItemIndex(taskList.value, task);
            switch (taskEventType) {
                case TaskEventTypeEnum.TASK_CHANGE:
                    if (taskItemIndex !== -1) {
                        taskList.value.splice(taskItemIndex, 1, task);
                    }
                    break;
                case TaskEventTypeEnum.TASK_NEW:
                    if (taskItemIndex === -1) {
                        taskList.value.unshift(task);
                    }
                    break;
            }
        } else {
            openTaskCenter();
            getTaskList(DEFAULT_TASK_NUM).then();
        }

        // if (taskNoticeVo !== null) {
        //     totalTask.value = taskNoticeVo.taskCount;
        // }
    };

    return {
        // 搜索关键词
        searchKeyword,
        setKeyword,
        /**
         * 任务是否加载中
         */
        isLoading,
        // 显示红点标记
        showNotice,
        /**
         * 任务列表数据
         */
        taskList,
        /**
         * 页面page数
         */
        listPage,
        /**
         * 初始化页面page数
         */
        initListPage,
        /**
         * 加载任务列表
         */
        getTaskList,
        /**
         * 清空任务列表(本地)
         */
        clearTaskList,
        /**
         * 清空任务列表(远程+本地)
         */
        remoteClearTaskList,
        /**
         * 删除任务
         */
        deleteTask,
        /**
         * 轮询拉取未读消息
         */
        getUnReadMsgNum,
        /**
         * 打开任务中心
         */
        openTaskCenter,
        /**
         * 关闭任务中心
         */
        closeTaskCenter,
        /**
         * 退出登录时清理任务中心本地状态
         */
        resetTaskCenterOnLogout,
        /**
         * 任务中心是否开启
         */
        isTaskCenterOpen,
        /**
         * 所有任务数量
         */
        totalTask,
        /**
         * 任务中心入口数字徽标文案
         */
        taskBadgeText,
        /**
         * 是否还有更多任务
         */
        hasMoreTask,
        /**
         * 下载结果文件
         */
        downloadTaskFile,
        /**
         * 初始化所有任务数量
         */
        initTotalTask,
        /**
         * 专用于接受ws通知更新任务
         */
        updateTask,
        isLoadMore,
        // 设置是否展开更多，按每页10条拉取
        setLoadMore
    };
});
